/* global BigInt */
// src/quoteMath.js
// Pure, tested math for the Quote Generator. Money is whole cents (src/money.js); margin uses the same
// rounding as the rest of the app (marginPercent in src/aiCharts.js).

import { marginPercent } from "./aiCharts";

// Reads what an owner typed. Accepts "1000", "1,000", "$1,000.50", " 1000 ", ".5", "12.", "-25" and
// numbers (older saved quotes store plain JS numbers; they are read from their shortest text form, so
// they go through the same exact path). Commas, dollar signs and spaces are removed. Empty text means 0.
// A leading minus is allowed here (supplier credits and discount lines are real); the caller decides
// where a negative is not allowed (revenue). Anything else (letters, two dots, "1e3", NaN) is unreadable.
// Returns { ok, value, num, scale }: value is the plain number (for display only); num is the typed
// digits as an exact BigInt and scale the count of decimal places, so the amount is num / 10^scale
// with no floating-point error. Unreadable input gives { ok: false, value: 0, num: 0n, scale: 0 }.
const BAD = { ok: false, value: 0, num: 0n, scale: 0 };
export function parseAmountText(text) {
  if (text === null || text === undefined) return { ok: true, value: 0, num: 0n, scale: 0 };
  let s;
  if (typeof text === "number") {
    if (!Number.isFinite(text)) return BAD;
    s = String(text);
    if (/e/i.test(s)) {
      if (Math.abs(text) >= 1e21) return BAD;
      s = text.toFixed(12); // tiny numbers like 1e-7
    }
  } else {
    s = String(text).replace(/[\s$,]/g, "");
  }
  if (s === "") return { ok: true, value: 0, num: 0n, scale: 0 };
  const m = /^(-?)(?:(\d+)\.?(\d*)|\.(\d+))$/.exec(s);
  if (!m) return BAD;
  const whole = m[2] !== undefined ? m[2] : "";
  const frac = m[2] !== undefined ? m[3] : m[4];
  const digits = BigInt((whole + frac) || "0");
  return { ok: true, value: Number(s) || 0, num: m[1] ? -digits : digits, scale: frac.length };
}

// Exact whole cents of the product of scaled amounts, rounded half away from zero once, at the end.
function centsOf(num, scale) {
  if (scale <= 2) return Number(num * 10n ** BigInt(2 - scale));
  const d = 10n ** BigInt(scale - 2);
  const abs = num < 0n ? -num : num;
  let q = abs / d;
  if ((abs % d) * 2n >= d) q += 1n;
  return Number(num < 0n ? -q : q);
}

// Whole cents of a typed dollar amount ("1,250.50" gives 125050). Unreadable text counts as 0.
export function textCents(text) {
  const a = parseAmountText(text);
  return centsOf(a.num, a.scale);
}

// Whole cents of one cost line. "flat": the amount. "unit": quantity x unit cost, multiplied as exact
// integers and rounded once to the cent. Unreadable fields count as 0 here; use quoteProblems to find them.
export function lineCents(line) {
  if (line && line.mode === "unit") {
    const q = parseAmountText(line.quantity), u = parseAmountText(line.unitCost);
    return centsOf(q.num * u.num, q.scale + u.scale);
  }
  const a = parseAmountText(line && line.amount);
  return centsOf(a.num, a.scale);
}

// Totals for a quote, all in whole cents, plus margin (a number with one decimal, or null with no revenue).
export function quoteTotals(costLines, expectedRevenueText) {
  const r = parseAmountText(expectedRevenueText);
  const revenueCents = centsOf(r.num, r.scale);
  const costCents = (costLines || []).reduce((s, l) => s + lineCents(l), 0);
  const profitCents = revenueCents - costCents;
  return { revenueCents, costCents, profitCents, margin: marginPercent(profitCents, revenueCents) };
}

// Fields the owner typed that can't be read, so they can be told instead of silently saving 0.
// Returns { revenue: boolean (unreadable), revenueNegative: boolean, lines: { [index]: ["amount" | "quantity" | "unitCost", ...] }, any: boolean }.
export function quoteProblems(costLines, expectedRevenueText) {
  const revenue = !parseAmountText(expectedRevenueText).ok;
  const lines = {};
  (costLines || []).forEach((l, i) => {
    const fields = l && l.mode === "unit" ? ["quantity", "unitCost"] : ["amount"];
    const bad = fields.filter((f) => !parseAmountText(l[f]).ok);
    if (bad.length) lines[i] = bad;
  });
  const revenueNegative = !revenue && quoteTotals([], expectedRevenueText).revenueCents < 0;
  return { revenue, revenueNegative, lines, any: revenue || revenueNegative || Object.keys(lines).length > 0 };
}

// Cost by category in whole cents, in the order of `categories`. Every non-zero line counts, credits
// (negative lines) included, and a line with a missing or unknown category goes under the last category
// ("Other"), so the category totals always add up to the quote's total cost.
// Returns [{ name, cents }] for categories whose total is not zero.
export function costByCategory(costLines, categories) {
  const other = categories[categories.length - 1];
  const sums = {};
  (costLines || []).forEach((l) => {
    const c = lineCents(l);
    if (c === 0) return;
    const name = categories.includes(l.category) ? l.category : other;
    sums[name] = (sums[name] || 0) + c;
  });
  return categories.map((name) => ({ name, cents: sums[name] || 0 })).filter((r) => r.cents !== 0);
}
